import {
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
	parseNumber,
} from './DataAccessUtils';
import { isWalthroughTaskAnswered, SITE_SURVEY_ANSWER_TYPE } from './SiteSurveyUtils';

export const ANSWER_DISPLAY_VARIANT = {
	SCORED: 'scored',
	UNSCORED: 'unscored',
	RESPONSE: 'response',
};

const SCORED_SCALE = [
	{ background: '#FFF8F8' },
	{ background: '#FFF9F4' },
	{ background: '#FFFDF3' },
	{ background: '#FEFFF3' },
	{ background: '#FAFFF6' },
];

const UNSCORED_PALETTE = [
	{ background: '#F8FFFF' },
	{ background: '#F8FDFF' },
	{ background: '#F8FBFF' },
	{ background: '#F8FAFF' },
	{ background: '#F8F8FF' },
	{ background: '#FAF8FF' },
	{ background: '#FCF8FF' },
	{ background: '#FEF8FF' },
	{ background: '#FFF8FF' },
	{ background: '#FFF8FD' },
	{ background: '#FAFCFD' },
	{ background: '#FCFCFC' },
];

export interface AnswerCellColors {
	background: string;
	text: string;
}

const hashString = (value: string): number => {
	let hash = 5381;
	for (let index = 0; index < value.length; index++) {
		hash = (hash * 33) ^ value.charCodeAt(index);
	}
	return hash >>> 0;
};

export const getAnswerDisplayVariant = (taskAnswer): string => {
	if (nullSafeGet('questionType', taskAnswer) !== SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE) {
		return ANSWER_DISPLAY_VARIANT.RESPONSE;
	}
	return parseNumber(nullSafeGetWithZero('maxMultipleChoiceValue', taskAnswer)) === null
		? ANSWER_DISPLAY_VARIANT.UNSCORED
		: ANSWER_DISPLAY_VARIANT.SCORED;
};

export const getScoredAnswerColors = (
	numericAnswer: number | null,
	minValue: number | null,
	maxValue: number | null
): AnswerCellColors | null => {
	if (numericAnswer === null || maxValue === null) return null;
	const low = minValue === null ? 0 : minValue;
	if (maxValue <= low) return SCORED_SCALE[SCORED_SCALE.length - 1];
	const ratio = Math.min(1, Math.max(0, (numericAnswer - low) / (maxValue - low)));
	return SCORED_SCALE[Math.round(ratio * (SCORED_SCALE.length - 1))];
};

/** Stable arbitrary colour for an unscored answer, keyed by its text. */
export const getUnscoredAnswerColors = (answer: string | null): AnswerCellColors | null => {
	if (!answer || !answer.trim()) return null;
	return UNSCORED_PALETTE[hashString(answer.trim()) % UNSCORED_PALETTE.length];
};

const PARENTHESISED_ANSWER = /^(\S+)\s*\((.*)\)\s*$/;

export const splitScoredAnswer = (
	answer: string | null
): { value: string | null; label: string | null } => {
	if (!answer) return { value: null, label: null };
	const trimmed = answer.trim();

	const parenthesised = trimmed.match(PARENTHESISED_ANSWER);
	if (parenthesised) {
		const label = parenthesised[2].trim();
		return {
			value: parenthesised[1],
			label: label && label !== parenthesised[1] ? label : null,
		};
	}

	const firstSpace = trimmed.indexOf(' ');
	if (firstSpace === -1) return { value: trimmed, label: null };
	return {
		value: trimmed.slice(0, firstSpace),
		label: trimmed.slice(firstSpace + 1).trim() || null,
	};
};

// The min/max choice values describe the QUESTION's scale, so an override that doesn't carry them
// (a past answer recorded before the choices were scored) still colours against the current scale.
const resolveScaleValue = (key: string, taskAnswer, answerOverride) => {
	const overrideValue = answerOverride ? answerOverride[key] : null;
	return overrideValue === null || overrideValue === undefined
		? nullSafeGetWithZero(key, taskAnswer)
		: overrideValue;
};

export const resolveAnswerValues = (taskAnswer, answerOverride?) => ({
	answer: answerOverride ? answerOverride.answer : nullSafeGet('answer', taskAnswer),
	numericAnswer: answerOverride
		? answerOverride.numericAnswer
		: nullSafeGetWithZero('numericAnswer', taskAnswer),
	minValue: resolveScaleValue('minMultipleChoiceValue', taskAnswer, answerOverride),
	maxValue: resolveScaleValue('maxMultipleChoiceValue', taskAnswer, answerOverride),
});

/** Stable colour key for non-scored cells: answer text, else question title / id (checklists). */
const getUnscoredColorKey = (taskAnswer, answer: string | null): string | null => {
	if (answer && String(answer).trim()) return String(answer).trim();
	const title = nullSafeGet('title', taskAnswer);
	if (title && String(title).trim()) return String(title).trim();
	const titleId = nullSafeGet('titleId', taskAnswer);
	return titleId === null || titleId === undefined || titleId === '' ? null : String(titleId);
};

const EXPANDED_CELL_COLORS: AnswerCellColors = { background: '#f0f4f5', text: 'inherit' };

export const getAnswerCellColors = (
	taskAnswer,
	answerOverride?,
	isExpanded?: boolean
): AnswerCellColors | null => {
	if (isExpanded) return EXPANDED_CELL_COLORS;
	// An unanswered cell stays uncoloured — `answerOverride` is the past walkthrough's own answer, so
	// each column is judged on what it holds rather than on the current walkthrough.
	if (!isWalthroughTaskAnswered(answerOverride || taskAnswer)) return null;

	const { answer, numericAnswer, minValue, maxValue } = resolveAnswerValues(
		taskAnswer,
		answerOverride
	);

	const variant = getAnswerDisplayVariant(taskAnswer);
	if (variant === ANSWER_DISPLAY_VARIANT.SCORED) {
		return getScoredAnswerColors(
			numericAnswer === undefined ? null : numericAnswer,
			minValue === undefined ? null : minValue,
			maxValue === undefined ? null : maxValue
		);
	}

	if (variant === ANSWER_DISPLAY_VARIANT.RESPONSE) {
		return null;
	}

	return getUnscoredAnswerColors(getUnscoredColorKey(taskAnswer, answer));
};

export const usesTaskItemAnswerDisplay = (taskAnswer): boolean => {
	const questionType = nullSafeGet('questionType', taskAnswer);
	return (
		questionType === SITE_SURVEY_ANSWER_TYPE.TASK_LIST ||
		questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST
	);
};

export const walkthroughHasScoredQuestions = (walkthrough): boolean =>
	nullSafeGetOrElse('taskAnswers', walkthrough, []).some(
		(taskAnswer) => getAnswerDisplayVariant(taskAnswer) === ANSWER_DISPLAY_VARIANT.SCORED
	);
